/**
 * AI-assisted relationship discovery — client for the streamed
 * `POST /relationships/propose-ai/{datasetId}` endpoint (ADR-0026 §4).
 *
 * The endpoint is SSE-over-POST (it carries a request body + the admin bearer
 * token), so `EventSource` can't be used — we read the response body stream by
 * hand, exactly like `lib/portalVoice.streamVoiceTurn`. The frame parser
 * (`decodeSseFrames`) is split out pure so the chunk-boundary handling is
 * unit-tested without a live network.
 *
 * Each event drives the **debugger**: `step`/`context` (what's being fed),
 * `thinking`/`delta` (the model's live output), `candidate`/`rejected` (results
 * + why one was dropped), `final` (the list + token usage).
 */
import { useAuthStore } from "./auth";
import type { AiRelationshipCandidate } from "./api-relationships";
import { consumeSse, parseSseFrames } from "./sseStream";

export type RelAiEvent =
  | { type: "step"; key: string; label: string }
  | {
      type: "context";
      dataset: string;
      field_count: number;
      catalog_count: number;
      existing_count: number;
      note?: string;
    }
  | { type: "thinking"; text: string }
  | { type: "delta"; text: string }
  | { type: "candidate"; candidate: AiRelationshipCandidate }
  | { type: "rejected"; raw: Record<string, unknown>; reason: string }
  | { type: "error"; message: string }
  | {
      type: "final";
      candidates: AiRelationshipCandidate[];
      usage: { input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number };
    }
  | { type: "done" };

/**
 * Pure SSE frame decoder: given the carry-over buffer + a freshly-read chunk,
 * return the parsed events for every COMPLETE frame and the remaining buffer
 * (an incomplete trailing frame). Frames are `\n\n`-separated; `data:` lines are
 * JSON. Malformed JSON in a frame is skipped, not thrown.
 */
export function decodeSseFrames(
  buffer: string,
  chunk: string,
): { events: RelAiEvent[]; buffer: string } {
  return parseSseFrames<RelAiEvent>(buffer, chunk);
}

/**
 * POST the proposal request and invoke `onEvent` for each SSE event until the
 * stream ends. Resolves when the stream closes; rejects only on a network error
 * (an HTTP error surfaces as an `error` event, then resolves).
 */
export async function streamProposeAi(
  datasetId: string,
  opts: { thinking?: boolean },
  onEvent: (ev: RelAiEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/admin/relationships/propose-ai/${datasetId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ thinking: !!opts.thinking }),
    signal,
  });
  if (!res.ok || !res.body) {
    // A 422 (AI unconfigured) carries a JSON body, not an SSE stream.
    let message = `http_${res.status}`;
    try {
      const j = (await res.json()) as { detail?: string; message?: string };
      message = j.detail || j.message || message;
    } catch {
      /* keep the http_<status> fallback */
    }
    onEvent({ type: "error", message });
    return;
  }
  await consumeSse<RelAiEvent>(res.body, onEvent);
}
